/**
 * Landing Pages API Client
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Helper to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
  };
};

// Landing Pages API
export const landingPagesApi = {
  // Templates
  getTemplates: async (category = null) => {
    const params = new URLSearchParams();
    if (category) params.append('category', category);
    const res = await fetch(`${BACKEND_URL}/api/v1/pages/templates?${params}`, {
      headers: getAuthHeaders()
    });
    return res.json();
  },

  getTemplate: async (id) => {
    const res = await fetch(`${BACKEND_URL}/api/v1/pages/templates/${id}`, {
      headers: getAuthHeaders()
    });
    return res.json();
  },

  createTemplate: async (data) => {
    const res = await fetch(`${BACKEND_URL}/api/v1/pages/templates`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    return res.json();
  },

  updateTemplate: async (id, data) => {
    const res = await fetch(`${BACKEND_URL}/api/v1/pages/templates/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    return res.json();
  },

  deleteTemplate: async (id) => {
    const res = await fetch(`${BACKEND_URL}/api/v1/pages/templates/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    return res.json();
  },

  saveAsTemplate: async (pageId, data) => {
    const res = await fetch(`${BACKEND_URL}/api/v1/pages/${pageId}/save-as-template`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    return res.json();
  },

  // CRUD
  create: async (data) => {
    const res = await fetch(`${BACKEND_URL}/api/v1/pages/`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    return res.json();
  },

  stats: async () => {
    const res = await fetch(`${BACKEND_URL}/api/v1/pages/stats`, {
      headers: getAuthHeaders()
    });
    return res.json();
  },

  list: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    const res = await fetch(`${BACKEND_URL}/api/v1/pages/?${query}`, {
      headers: getAuthHeaders()
    });
    return res.json();
  },

  get: async (id) => {
    const res = await fetch(`${BACKEND_URL}/api/v1/pages/${id}`, {
      headers: getAuthHeaders()
    });
    return res.json();
  },

  update: async (id, data) => {
    const res = await fetch(`${BACKEND_URL}/api/v1/pages/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    return res.json();
  },

  delete: async (id) => {
    const res = await fetch(`${BACKEND_URL}/api/v1/pages/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    return res.json();
  },

  publish: async (id) => {
    const res = await fetch(`${BACKEND_URL}/api/v1/pages/${id}/publish`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    return res.json();
  },

  unpublish: async (id) => {
    const res = await fetch(`${BACKEND_URL}/api/v1/pages/${id}/unpublish`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    return res.json();
  },

  duplicate: async (id) => {
    const res = await fetch(`${BACKEND_URL}/api/v1/pages/${id}/duplicate`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    return res.json();
  },

  // Sections
  updateSections: async (id, sections) => {
    const res = await fetch(`${BACKEND_URL}/api/v1/pages/${id}/sections`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(sections)
    });
    return res.json();
  },

  // Form
  updateForm: async (id, form) => {
    const res = await fetch(`${BACKEND_URL}/api/v1/pages/${id}/form`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(form)
    });
    return res.json();
  },
};

// Submissions API
export const submissionsApi = {
  listAll: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    const res = await fetch(`${BACKEND_URL}/api/v1/submissions/?${query}`, {
      headers: getAuthHeaders()
    });
    return res.json();
  },

  listByPage: async (pageId, params = {}) => {
    const query = new URLSearchParams(params).toString();
    const res = await fetch(`${BACKEND_URL}/api/v1/pages/${pageId}/submissions?${query}`, {
      headers: getAuthHeaders()
    });
    return res.json();
  },

  get: async (id) => {
    const res = await fetch(`${BACKEND_URL}/api/v1/submissions/${id}`, {
      headers: getAuthHeaders()
    });
    return res.json();
  },

  markRead: async (id, read = true) => {
    const res = await fetch(`${BACKEND_URL}/api/v1/submissions/${id}/read?read=${read}`, {
      method: 'PUT',
      headers: getAuthHeaders()
    });
    return res.json();
  },

  star: async (id, starred = true) => {
    const res = await fetch(`${BACKEND_URL}/api/v1/submissions/${id}/star?starred=${starred}`, {
      method: 'PUT',
      headers: getAuthHeaders()
    });
    return res.json();
  },

  archive: async (id) => {
    const res = await fetch(`${BACKEND_URL}/api/v1/submissions/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    return res.json();
  },

  stats: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    const res = await fetch(`${BACKEND_URL}/api/v1/submissions/stats?${query}`, {
      headers: getAuthHeaders()
    });
    return res.json();
  },

  filterOptions: async () => {
    const res = await fetch(`${BACKEND_URL}/api/v1/submissions/filter-options`, {
      headers: getAuthHeaders()
    });
    return res.json();
  },

  // Downloads the filtered submissions as CSV. Resolves to
  // { rows, total, truncated } from the response headers.
  exportCsv: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    const res = await fetch(`${BACKEND_URL}/api/v1/submissions/export?${query}`, {
      headers: getAuthHeaders()
    });
    if (!res.ok) throw new Error(`Export failed (${res.status})`);
    const blob = await res.blob();
    const disposition = res.headers.get('Content-Disposition') || '';
    const filename = (disposition.match(/filename="([^"]+)"/) || [])[1] || 'linkhub-submissions.csv';
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    return {
      rows: Number(res.headers.get('X-Export-Rows') || 0),
      total: Number(res.headers.get('X-Export-Total') || 0),
      truncated: res.headers.get('X-Export-Truncated') === 'true',
    };
  }
};

// Brand Settings API
export const brandApi = {
  getSettings: async () => {
    const res = await fetch(`${BACKEND_URL}/api/v1/brand/settings`, {
      headers: getAuthHeaders()
    });
    return res.json();
  },

  updateSettings: async (data) => {
    const res = await fetch(`${BACKEND_URL}/api/v1/brand/settings`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    return res.json();
  },

  resetToDefaults: async () => {
    const res = await fetch(`${BACKEND_URL}/api/v1/brand/settings/reset`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    return res.json();
  },

  getDefaults: async () => {
    const res = await fetch(`${BACKEND_URL}/api/v1/brand/defaults`, {
      headers: getAuthHeaders()
    });
    return res.json();
  }
};
